// pages/Account.jsx
import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { db } from "../services/firebase";
import { doc, getDoc, setDoc, collection, getDocs, orderBy, query } from "firebase/firestore";
import Navigation from "../components/Common/header/Navigation";
import Footer from "../components/Common/footer/Footer";
import "../styles/Auth.css";
import "../styles/Account.css";

const Account = () => {
    const { currentUser, login, signup, logout } = useAuth();
    const [isLoginMode, setIsLoginMode] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [addressLoading, setAddressLoading] = useState(false);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [orders, setOrders] = useState([]);

    // Auth form state
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [displayName, setDisplayName] = useState("");

    // Address state
    const [addresses, setAddresses] = useState([]);
    const [showAddressForm, setShowAddressForm] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);
    const [addressForm, setAddressForm] = useState({
        label: "", fullName: "", line1: "", line2: "",
        city: "", county: "", postcode: "", country: "United Kingdom"
    });

    // ── Load addresses from Firestore ──
    useEffect(() => {
        if (!currentUser) return;
        const fetchAddresses = async () => {
            setAddressLoading(true);
            try {
                const ref = doc(db, "users", currentUser.uid);
                const snap = await getDoc(ref);
                if (snap.exists() && snap.data().addresses) {
                    setAddresses(snap.data().addresses);
                }
            } catch (err) { console.error("Failed to load addresses:", err); }
            setAddressLoading(false);
        };
        fetchAddresses();
    }, [currentUser]);

    // ── Load orders from Firestore ──
    useEffect(() => {
        if (!currentUser) return;
        const fetchOrders = async () => {
            setOrdersLoading(true);
            try {
                const ordersRef = collection(db, "users", currentUser.uid, "orders");
                const q = query(ordersRef, orderBy("createdAt", "desc"));
                const snap = await getDocs(q);
                setOrders(snap.docs.map(d => ({ firestoreId: d.id, ...d.data() })));
            } catch (err) { console.error("Failed to load orders:", err); }
            setOrdersLoading(false);
        };
        fetchOrders();
    }, [currentUser]);

    const saveAddressesToFirestore = async (updated) => {
        try {
            const ref = doc(db, "users", currentUser.uid);
            await setDoc(ref, { addresses: updated }, { merge: true });
        } catch (err) { console.error("Failed to save addresses:", err); }
    };

    const toggleMode = () => { setIsLoginMode(!isLoginMode); setError(""); };

    const handleLoginSubmit = async (e) => {
        e.preventDefault();
        try {
            setError(""); setLoading(true);
            await login(email, password);
        } catch (err) { setError("Failed to sign in: " + err.message); }
        setLoading(false);
    };

    const handleRegisterSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirmPassword) return setError("Passwords do not match");
        try {
            setError(""); setLoading(true);
            await signup(email, password, displayName);
        } catch (err) { setError("Failed to create an account: " + err.message); }
        setLoading(false);
    };

    const handleLogout = async () => {
        try { await logout(); } catch (err) { setError("Failed to log out: " + err.message); }
    };

    const openAddressForm = (index = null) => {
        if (index !== null) {
            setAddressForm(addresses[index]);
            setEditingIndex(index);
        } else {
            setAddressForm({ label: "", fullName: "", line1: "", line2: "", city: "", county: "", postcode: "", country: "United Kingdom" });
            setEditingIndex(null);
        }
        setShowAddressForm(true);
    };

    const handleAddressChange = (e) => setAddressForm({ ...addressForm, [e.target.name]: e.target.value });

    const handleAddressSave = async (e) => {
        e.preventDefault();
        let updated;
        if (editingIndex !== null) {
            updated = [...addresses];
            updated[editingIndex] = addressForm;
        } else {
            updated = [...addresses, addressForm];
        }
        setAddresses(updated);
        await saveAddressesToFirestore(updated);
        setShowAddressForm(false);
    };

    const handleAddressDelete = async (index) => {
        const updated = addresses.filter((_, i) => i !== index);
        setAddresses(updated);
        await saveAddressesToFirestore(updated);
    };

    // --- Helper Functions ---
    const formatDate = (ts) => {
        if (!ts) return "—";
        const date = ts.toDate ? ts.toDate() : new Date(ts);
        return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    };

    // Formats the long Stripe ID into a clean 8-character string
    const formatOrderId = (id) => {
        if (!id) return "UNKNOWN";
        if (id.startsWith("cs_")) {
            // Takes the random string at the end of cs_test_ and grabs 8 characters
            return id.split("_").pop().slice(-8).toUpperCase();
        }
        return id.slice(0, 8).toUpperCase();
    };

    // ── Logged in view ──
    if (currentUser) {
        return (
            <div className="auth-page">
                <Navigation />
                <div className="container py-5">
                    <div className="account-header">
                        <h1 className="account-welcome">
                            Welcome back{currentUser.displayName ? `, ${currentUser.displayName.split(' ')[0]}` : ""}
                        </h1>
                        <div className="account-divider" />
                    </div>

                    <div className="account-grid">

                        {/* ── Account Details ── */}
                        <div className="account-panel">
                            <h3 className="account-panel-title">Account Details</h3>
                            <div className="account-detail-row">
                                <span className="account-detail-label">Name</span>
                                <span className="account-detail-value">{currentUser.displayName || "Not set"}</span>
                            </div>
                            <div className="account-detail-row">
                                <span className="account-detail-label">Email</span>
                                <span className="account-detail-value">{currentUser.email}</span>
                            </div>
                            <div className="account-detail-row">
                                <span className="account-detail-label">Member ID</span>
                                <span className="account-detail-value account-uid">{currentUser.uid}</span>
                            </div>
                            <button className="account-btn-outline" onClick={handleLogout}>Log Out</button>
                        </div>

                        {/* ── Order History ── */}
                        <div className="account-panel">
                            <h3 className="account-panel-title">Order History</h3>
                            {ordersLoading && <p className="account-empty">Loading orders...</p>}
                            {!ordersLoading && orders.length === 0 && (
                                <>
                                    <p className="account-empty">You haven't placed any orders yet.</p>
                                    <div className="account-links">
                                        <a href="/shop" className="account-link">Continue Shopping</a>
                                        <a href="/cart" className="account-link">View Cart</a>
                                    </div>
                                </>
                            )}
                            {!ordersLoading && orders.length > 0 && (
                                <div className="order-list">
                                    {orders.map((order) => {
                                        // Safely check if the shipping address exists
                                        const ship = order.shippingAddress;
                                        const hasAddress = ship && ship.address && ship.address.trim() !== "";

                                        return (
                                            <div key={order.firestoreId} className="order-row">
                                                <div className="order-row-top">
                                                    {/* Using our new ID formatter! */}
                                                    <span className="order-id">#{formatOrderId(order.id || order.firestoreId)}</span>
                                                    <span className="order-date">{formatDate(order.createdAt)}</span>
                                                </div>
                                                <div className="order-row-bottom">
                                                    <span className="order-items-summary">
                                                        {order.items?.length
                                                            ? `${order.items.length} item${order.items.length > 1 ? "s" : ""}`
                                                            : "Order"}
                                                    </span>
                                                    <span className="order-total">£{Number(order.total).toFixed(2)}</span>
                                                </div>
                                                <div className="order-shipping-preview" style={{ color: "#a0aec0", fontSize: "0.85rem", marginTop: "8px" }}>
                                                    {/* Gracefully handle empty shipping addresses */}
                                                    {hasAddress
                                                        ? `${ship.address}, ${ship.city} ${ship.zipCode}`
                                                        : "Shipping address managed via Stripe / Express Checkout"}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* ── Saved Addresses ── */}
                        <div className="account-panel account-panel--full">
                            <div className="account-panel-header">
                                <h3 className="account-panel-title">Saved Addresses</h3>
                                {!showAddressForm && (
                                    <button className="account-btn-gold" onClick={() => openAddressForm()}>
                                        + Add Address
                                    </button>
                                )}
                            </div>

                            {addressLoading && <p className="account-empty">Loading addresses...</p>}

                            {showAddressForm && (
                                <form className="address-form" onSubmit={handleAddressSave}>
                                    <div className="address-form-grid">
                                        <div className="auth-field address-field--full">
                                            <label className="auth-label">Address Label (e.g. Home, Work)</label>
                                            <input name="label" className="auth-input" value={addressForm.label} onChange={handleAddressChange} placeholder="Home" required />
                                        </div>
                                        <div className="auth-field">
                                            <label className="auth-label">Full Name</label>
                                            <input name="fullName" className="auth-input" value={addressForm.fullName} onChange={handleAddressChange} required />
                                        </div>
                                        <div className="auth-field">
                                            <label className="auth-label">Address Line 1</label>
                                            <input name="line1" className="auth-input" value={addressForm.line1} onChange={handleAddressChange} required />
                                        </div>
                                        <div className="auth-field">
                                            <label className="auth-label">Address Line 2 (optional)</label>
                                            <input name="line2" className="auth-input" value={addressForm.line2} onChange={handleAddressChange} />
                                        </div>
                                        <div className="auth-field">
                                            <label className="auth-label">City</label>
                                            <input name="city" className="auth-input" value={addressForm.city} onChange={handleAddressChange} required />
                                        </div>
                                        <div className="auth-field">
                                            <label className="auth-label">County</label>
                                            <input name="county" className="auth-input" value={addressForm.county} onChange={handleAddressChange} />
                                        </div>
                                        <div className="auth-field">
                                            <label className="auth-label">Postcode</label>
                                            <input name="postcode" className="auth-input" value={addressForm.postcode} onChange={handleAddressChange} required />
                                        </div>
                                        <div className="auth-field">
                                            <label className="auth-label">Country</label>
                                            <input name="country" className="auth-input" value={addressForm.country} onChange={handleAddressChange} required />
                                        </div>
                                    </div>
                                    <div className="address-form-actions">
                                        <button type="submit" className="account-btn-gold">
                                            {editingIndex !== null ? "Save Changes" : "Save Address"}
                                        </button>
                                        <button type="button" className="account-btn-outline" onClick={() => setShowAddressForm(false)}>
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            )}

                            {!addressLoading && addresses.length === 0 && !showAddressForm && (
                                <p className="account-empty">No saved addresses yet. Add one for faster checkout.</p>
                            )}

                            <div className="address-cards">
                                {addresses.map((addr, i) => (
                                    <div key={i} className="address-card">
                                        <span className="address-card-label">{addr.label || "Address"}</span>
                                        <p className="address-card-text">
                                            {addr.fullName}<br />
                                            {addr.line1}{addr.line2 ? `, ${addr.line2}` : ""}<br />
                                            {addr.city}{addr.county ? `, ${addr.county}` : ""}<br />
                                            {addr.postcode}<br />
                                            {addr.country}
                                        </p>
                                        <div className="address-card-actions">
                                            <button className="address-card-btn" onClick={() => openAddressForm(i)}>Edit</button>
                                            <button className="address-card-btn address-card-btn--delete" onClick={() => handleAddressDelete(i)}>Remove</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    // ── Logged out view ──
    return (
        <div className="auth-page">
            <Navigation />
            <div className="container py-5">
                <div className="auth-card">
                    <h2 className="auth-title">{isLoginMode ? "Log In" : "Create Account"}</h2>
                    <div className="auth-divider" />
                    {error && <div className="auth-error">{error}</div>}
                    <form onSubmit={isLoginMode ? handleLoginSubmit : handleRegisterSubmit}>
                        {!isLoginMode && (
                            <div className="auth-field">
                                <label htmlFor="displayName" className="auth-label">Full Name</label>
                                <input type="text" className="auth-input" id="displayName" value={displayName} onChange={(e) => setDisplayName(e.target.value)} required={!isLoginMode} />
                            </div>
                        )}
                        <div className="auth-field">
                            <label htmlFor="email" className="auth-label">Email</label>
                            <input type="email" className="auth-input" id="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                        </div>
                        <div className="auth-field">
                            <label htmlFor="password" className="auth-label">Password</label>
                            <input type="password" className="auth-input" id="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                        </div>
                        {!isLoginMode && (
                            <div className="auth-field">
                                <label htmlFor="confirmPassword" className="auth-label">Confirm Password</label>
                                <input type="password" className="auth-input" id="confirmPassword" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required={!isLoginMode} />
                            </div>
                        )}
                        <button disabled={loading} className="auth-btn" type="submit">
                            {loading ? (isLoginMode ? "Logging in..." : "Creating Account...") : (isLoginMode ? "Log In" : "Create Account")}
                        </button>
                    </form>
                    <p className="auth-footer-text">
                        {isLoginMode ? "Need an account? " : "Already have an account? "}
                        <button type="button" className="auth-toggle-btn" onClick={toggleMode}>
                            {isLoginMode ? "Sign Up" : "Log In"}
                        </button>
                    </p>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default Account;